(function () {
  'use strict';

  const STORAGE_KEY = 'dresscode:favourites';

  // Dresses saved before the stored width was raised carry a small image URL.
  // Rewriting the width keeps those cards as sharp as freshly saved ones,
  // without having to migrate what's already in storage.
  function upgradeImageWidth(url, width) {
    if (!url) return url;
    return url.replace(/([?&]width=)\d+/, `$1${width}`);
  }

  // "Remove all" asks twice: clearing a shortlist is easy to do by accident and
  // impossible to undo. The button reverts to its normal label after a moment.
  function setUpClearButtons() {
    let resetTimer;

    function reset(button) {
      if (!button || button.dataset.confirming !== 'true') return;
      button.dataset.confirming = 'false';
      button.textContent = button.dataset.idleLabel || 'Remove all';
      button.classList.remove('favourites-clear--confirming');
    }

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-favourites-clear]');
      document.querySelectorAll('[data-favourites-clear]').forEach((other) => {
        if (other !== button) reset(other);
      });
      if (!button) return;

      event.preventDefault();
      clearTimeout(resetTimer);

      if (button.dataset.confirming === 'true') {
        reset(button);
        writeFavourites([]);
        return;
      }

      button.dataset.idleLabel = button.textContent.trim();
      button.dataset.confirming = 'true';
      button.textContent = button.dataset.confirmLabel || 'Tap again to remove all';
      button.classList.add('favourites-clear--confirming');
      resetTimer = setTimeout(() => reset(button), 4000);
    });
  }

  function readFavourites() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (error) {
      return [];
    }
  }

  function writeFavourites(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      /* storage unavailable (private mode) — feature degrades silently */
    }
    document.dispatchEvent(new CustomEvent('favourites:changed'));
  }

  function isFavourite(handle) {
    return readFavourites().some((item) => item.handle === handle);
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }

  function updateShortlistLink(anchor, dataset, items) {
    if (!anchor) return;
    anchor.hidden = items.length === 0;
    if (!items.length) return;
    const lines = [dataset.message || ''].concat(
      items.map((item) => `- ${item.title} ${window.location.origin}${item.url}`)
    );
    anchor.href = `https://wa.me/${dataset.whatsappNumber}?text=${encodeURIComponent(lines.join('\n'))}`;
  }

  function trackFavourite(action, item) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('dress_favourite', { action: action, handle: item.handle, title: item.title });
      }
      if (action === 'add') {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'add_to_wishlist', { items: [{ item_id: item.handle, item_name: item.title }] });
        }
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'AddToWishlist', {
            content_name: item.title,
            content_ids: [item.handle],
            content_type: 'product',
          });
        }
      }
      const webhook = window.dresscodeFavouritesWebhook;
      if (webhook && navigator.sendBeacon) {
        const payload = JSON.stringify({
          action: action,
          handle: item.handle,
          title: item.title,
          url: item.url,
          at: new Date().toISOString(),
        });
        navigator.sendBeacon(webhook, new Blob([payload], { type: 'application/json' }));
      }
    } catch (error) {
      /* tracking must never break the UI */
    }
  }

  class FavouriteToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.refresh = this.refresh.bind(this);
      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
      });
      document.addEventListener('favourites:changed', this.refresh);
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('favourites:changed', this.refresh);
    }

    get item() {
      return {
        handle: this.dataset.handle,
        title: this.dataset.title,
        url: this.dataset.url,
        image: this.dataset.image,
      };
    }

    toggle() {
      const items = readFavourites();
      if (isFavourite(this.dataset.handle)) {
        writeFavourites(items.filter((item) => item.handle !== this.dataset.handle));
        trackFavourite('remove', this.item);
      } else {
        items.push(this.item);
        writeFavourites(items);
        trackFavourite('add', this.item);
      }
    }

    refresh() {
      const active = isFavourite(this.dataset.handle);
      this.classList.toggle('is-active', active);
      if (this.button) this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }
  customElements.define('favourite-toggle', FavouriteToggle);

  class FavouritesList extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-favourites-list]');
      this.empty = this.querySelector('[data-favourites-empty]');
      this.whatsapp = this.querySelector('[data-favourites-whatsapp]');
      this.footer = this.querySelector('[data-favourites-footer]');
      this.render = this.render.bind(this);
      document.addEventListener('favourites:changed', this.render);
      this.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-remove-handle]');
        if (!removeButton) return;
        writeFavourites(readFavourites().filter((item) => item.handle !== removeButton.dataset.removeHandle));
      });
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('favourites:changed', this.render);
    }

    render() {
      const items = readFavourites();
      if (this.empty) this.empty.hidden = items.length > 0;
      if (this.whatsapp) this.whatsapp.hidden = items.length === 0;
      if (this.footer) this.footer.hidden = items.length === 0;
      if (this.list) {
        this.list.innerHTML = items
          .map(
            (item) => `
              <li class="favourites-modal__item">
                ${item.image ? `<img class="favourites-modal__image" src="${escapeHtml(item.image)}" alt="" loading="lazy" width="60" height="80">` : ''}
                <a href="${escapeHtml(item.url)}" class="favourites-modal__item-title">${escapeHtml(item.title)}</a>
                <button type="button" class="favourites-modal__remove" data-remove-handle="${escapeHtml(item.handle)}" aria-label="Remove ${escapeHtml(item.title)}">&times;</button>
              </li>`
          )
          .join('');
      }
      updateShortlistLink(this.whatsapp, this.dataset, items);
    }
  }
  customElements.define('favourites-list', FavouritesList);

  class FavouritesGrid extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-favourites-grid]');
      this.empty = this.querySelector('[data-favourites-empty]');
      this.actions = this.querySelector('[data-favourites-actions]');
      this.whatsapp = this.querySelector('[data-favourites-whatsapp]');
      this.footer = this.querySelector('[data-favourites-footer]');
      this.render = this.render.bind(this);
      document.addEventListener('favourites:changed', this.render);
      this.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-remove-handle]');
        if (!removeButton) return;
        writeFavourites(readFavourites().filter((item) => item.handle !== removeButton.dataset.removeHandle));
      });
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('favourites:changed', this.render);
    }

    // Simple markup, used when the real card can't be fetched (offline, a
    // deleted product, or an old browser). Keeps the page usable either way.
    fallbackCard(item) {
      return `
        <li class="grid__item favourites-grid__item">
          <a href="${escapeHtml(item.url)}" class="favourites-grid__media">
            ${item.image ? `<img src="${escapeHtml(upgradeImageWidth(item.image, 720))}" alt="${escapeHtml(item.title)}" loading="lazy" width="720" height="960">` : ''}
          </a>
          <button type="button" class="favourite-toggle__button favourites-grid__remove" data-remove-handle="${escapeHtml(item.handle)}" aria-label="Remove ${escapeHtml(item.title)}">&times;</button>
          <a href="${escapeHtml(item.url)}" class="favourites-grid__title">${escapeHtml(item.title)}</a>
        </li>`;
    }

    // The real product card, straight from the theme's card snippet, so this
    // page always matches the collection grid.
    async fetchCard(item) {
      try {
        const response = await fetch(`/products/${encodeURIComponent(item.handle)}?section_id=favourite-card`);
        if (!response.ok) return null;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        return doc.querySelector('[data-favourite-card]');
      } catch (error) {
        return null;
      }
    }

    async render() {
      const items = readFavourites();
      if (this.empty) this.empty.hidden = items.length > 0;
      if (this.actions) this.actions.hidden = items.length === 0;
      if (this.footer) this.footer.hidden = items.length === 0;
      updateShortlistLink(this.whatsapp, this.dataset, items);
      if (!this.grid) return;

      // Ignore a render that started earlier than one already in flight.
      const token = {};
      this.renderToken = token;

      const cards = await Promise.all(items.map((item) => this.fetchCard(item)));
      if (this.renderToken !== token) return;

      this.grid.innerHTML = '';
      items.forEach((item, index) => {
        const card = cards[index];
        if (card) {
          this.grid.appendChild(document.importNode(card, true));
        } else {
          this.grid.insertAdjacentHTML('beforeend', this.fallbackCard(item));
        }
      });
    }
  }
  customElements.define('favourites-grid', FavouritesGrid);

  function updateCounters() {
    const count = readFavourites().length;
    document.querySelectorAll('[data-favourites-count]').forEach((element) => {
      element.hidden = count === 0;
      const span = element.querySelector('span');
      if (span) span.textContent = count < 100 ? String(count) : '99+';
    });
  }
  document.addEventListener('favourites:changed', updateCounters);
  updateCounters();
  setUpClearButtons();
})();
